import { normalizePathForComparison, getCredentialVaultNavSideFromPath } from '../jsx/layouts/nav/moduleNav';
import { canDo } from './roleAccess';

/**
 * Context-aware "Add" destination for the universal navbar plus button.
 * @param {string} pathname
 * @param {string} [search]
 * @param {object|null} [locationState]
 * @returns {{ to: string, title: string, section?: string, action?: string } | null}
 */
export function getUniversalAddAction(pathname, search = '', locationState = null) {
  const path = normalizePathForComparison(pathname).toLowerCase();
  if (!path) return null;

  // Already on a create/edit form — hide Add
  if (
    path.includes('/add') ||
    path.includes('/create') ||
    path.includes('/upload') ||
    path.includes('/edit') ||
    path.endsWith('/register') ||
    path === '/login' ||
    path === '/dashboard'
  ) {
    return null;
  }

  // Tasks module (list, details, worksheet, board, performance)
  if (
    path === '/task/tasklist' ||
    path.startsWith('/task/taskdetails') ||
    path === '/task/worksheet' ||
    path === '/task/taskboard' ||
    path.startsWith('/task/performance')
  ) {
    return { to: '/task/addtask', title: 'New Task', section: 'tasks', action: 'create_edit' };
  }

  // Project tasks / dashboard — create task or project depending on context
  if (path === '/task/projecttasklist') {
    return { to: '/task/addtask', title: 'New Task', section: 'tasks', action: 'create_edit' };
  }
  if (path === '/task/projectdashboard') {
    return { to: '/task/addproject', title: 'New Project', section: 'projects', action: 'create_edit' };
  }

  // Projects
  if (path === '/task/projectlist' || path.startsWith('/task/projectdetails')) {
    return { to: '/task/addproject', title: 'New Project', section: 'projects', action: 'create_edit' };
  }

  // Forum
  if (path === '/forum/dashboard' || path === '/forum/list' || path.startsWith('/forum/detail')) {
    return { to: '/forum/create', title: 'Start conversation', section: 'forum', action: 'create_edit' };
  }

  // Document classifications
  if (path === '/documents/documentclassifications') {
    return {
      to: '/documents/addDocumentClassification',
      title: 'New Classification',
      section: 'doc_classifications',
      action: 'create_edit',
    };
  }

  // Documents (browse / shared / favorites / dashboard / details)
  if (
    path === '/documents/mydocuments' ||
    path.startsWith('/documents/folder/') ||
    path === '/documents/shared' ||
    path === '/documents/favorites' ||
    path === '/documents/dashboard' ||
    path.startsWith('/documents/details/')
  ) {
    return { to: '/documents/upload', title: 'Upload Document', section: 'documents', action: 'create_edit' };
  }

  // Credential vault
  if (path.startsWith('/credential-vault/')) {
    if (path.includes('vaultsettings')) return null;
    const side = getCredentialVaultNavSideFromPath(pathname, search, locationState);
    if (side === 'personal') {
      return {
        to: '/credential-vault/addCredential?scope=personal',
        title: 'Add credential',
        section: 'credentials',
        action: 'create_edit',
      };
    }
    if (
      path.includes('credentialvaultlist') ||
      path.includes('personalvaultlist') ||
      path.includes('credentialdetail')
    ) {
      return {
        to: '/credential-vault/addCredential',
        title: 'Add credential',
        section: 'credentials',
        action: 'create_edit',
      };
    }
  }

  // Users
  if (path === '/settings/flowdoroles') {
    return { to: '/settings/flowdoRoles/new', title: 'New Role', section: 'users', action: 'create_edit' };
  }

  if (path === '/settings/userlist' || path.startsWith('/settings/user')) {
    return { to: '/settings/adduser', title: 'Add User', section: 'users', action: 'create_edit' };
  }

  // Org settings lists
  if (path === '/settings/branchlist') {
    return { to: '/settings/addBranch', title: 'Add Branch', section: 'settings', action: 'create_edit' };
  }
  if (path === '/settings/departmentlist') {
    return { to: '/settings/addDepartment', title: 'Add Department', section: 'settings', action: 'create_edit' };
  }
  if (path === '/settings/subdepartmentlist') {
    return { to: '/settings/addSubDepartment', title: 'Add Sub-Department', section: 'settings', action: 'create_edit' };
  }
  if (path === '/settings/designationlist') {
    return { to: '/settings/addDesignation', title: 'Add Designation', section: 'settings', action: 'create_edit' };
  }

  // Staff
  if (path === '/staff/stafflist' || (path.startsWith('/staff/') && !path.includes('/add'))) {
    if (path.includes('/address/') || path.includes('/contact/') || path.includes('/employment/') || path.includes('/document/')) {
      return null;
    }
    return { to: '/staff/addStaff', title: 'Add Staff', section: 'staff', action: 'create_edit' };
  }

  // Vehicles
  if (path === '/vehicle/vehiclelist' || (path.startsWith('/vehicle/') && !path.includes('/add'))) {
    if (
      path.includes('/document/') ||
      path.includes('/insurance/') ||
      path.includes('/registration/')
    ) {
      return null;
    }
    return { to: '/vehicle/addVehicle', title: 'Add Vehicle', section: 'vehicles', action: 'create_edit' };
  }

  return null;
}

export function getPermittedUniversalAddAction(pathname, search = '', locationState = null) {
  const action = getUniversalAddAction(pathname, search, locationState);
  if (!action) return null;
  if (action.section && action.action && !canDo(action.section, action.action)) {
    return null;
  }
  return action;
}
